import {
  MIS_3005_D_TABLE_1,
  RULES_BUNDLE_VERSION,
} from "@sts/rules-data";
import type {
  Adjacency,
  CalcContext,
  CalcStamp,
  CalcWarning,
  Dwelling,
  Room,
  Surface,
} from "./types.js";

export const ENGINE_VERSION = "0.1.0";

/** CIBSE Domestic Heating Design Guide volumetric heat capacity of air, Wh/(m³·K). */
export const DEFAULT_AIR_VOLUMETRIC_HEAT_CAPACITY = 0.33;

export interface SurfaceHeatLoss {
  readonly surfaceId: string;
  readonly name?: string;
  readonly kind: Surface["kind"];
  readonly areaM2: number;
  readonly uValue: number;
  /** Temperature difference applied to this surface, K. */
  readonly deltaTK: number;
  /** Transmission loss through this surface, W. */
  readonly heatLossW: number;
}

export interface RoomHeatLoss {
  readonly roomId: string;
  readonly name: string;
  readonly designTempC: number;
  readonly floorAreaM2: number;
  readonly volumeM3: number;
  readonly surfaces: readonly SurfaceHeatLoss[];
  readonly transmissionW: number;
  readonly thermalBridgingW: number;
  readonly ventilationW: number;
  /**
   * What set the ventilation loss: the room's air changes, or its continuous
   * extract when that takes out more (`Room.continuousExtractM3h`).
   */
  readonly ventilationBasis?: "air-changes" | "continuous-extract";
  /** The ventilation air flow used, m³/h. */
  readonly ventilationAirM3h?: number;
  readonly totalW: number;
  /** Required by MIS 3005-D §3.8.1(a): "all specific room heat losses in W/m²". */
  readonly wattsPerM2: number;
}

/**
 * A room a person marked as unheated, kept out of the heat load by name.
 *
 * It is reported rather than dropped silently: a room that vanished from a
 * heat loss with no trace is exactly the failure the rest of this schema is
 * built against, and an auditor comparing the plan with the calculation has to
 * be able to see why the garage is not in the table.
 */
export interface ExcludedRoom {
  readonly roomId: string;
  readonly name: string;
  readonly floorAreaM2: number;
  /** The designer's words, when they gave any. */
  readonly reason?: string;
}

export interface DwellingHeatLoss {
  readonly dwellingId: string;
  /** The HEATED rooms. An unheated room is in `excludedRooms` instead. */
  readonly rooms: readonly RoomHeatLoss[];
  /**
   * Rooms left out of every figure above because somebody marked them
   * unheated. Their floor area is excluded from `totalFloorAreaM2` too, so
   * `wattsPerM2` is watts per square metre of HEATED floor.
   */
  readonly excludedRooms: readonly ExcludedRoom[];
  readonly totalTransmissionW: number;
  readonly totalThermalBridgingW: number;
  readonly totalVentilationW: number;
  /** The design heat load the heat pump must meet at least 100% of. */
  readonly totalHeatLossW: number;
  readonly totalFloorAreaM2: number;
  readonly wattsPerM2: number;
  readonly warnings: readonly CalcWarning[];
  readonly stamp: CalcStamp;
  readonly context: CalcContext;
}

/**
 * Resolves the temperature on the far side of a building element.
 *
 * MIS 3005-D:2025 §3.4.1(c): solid ground floors use the local ANNUAL AVERAGE
 * external air temperature, not the design external temperature.
 * §3.4.1(d): suspended floors use the design external air temperature.
 * (Issue 2.0, 05/12/2025, p.11. §3.4.2(b)/(c), p.12, and §3.4.3(b)/(c), p.13,
 * say the same for hybrid and air-to-air systems; that is where the (b)/(c)
 * lettering comes from.)
 */
function otherSideTemperatureC(adjacency: Adjacency, ctx: CalcContext): number {
  switch (adjacency.kind) {
    case "external":
    case "suspended-floor":
      return ctx.externalDesignTempC;
    case "ground":
      return ctx.annualAverageExternalTempC;
    // Always the stated figure, never a default. MIS 3005-D:2025 sets no
    // temperature for an adjoining dwelling or an unheated space: §3.4.1(a)
    // (Issue 1.0 p.10; Issue 2.0 p.11) says only that the calculation "shall in
    // other respects comply with BS EN 12831-1:2017", and Tables 1-2 (p.12 /
    // p.14) cover rooms and outside air only. MCS's Heat Load Calculator takes
    // these from the CIBSE DHDG (2026). So the designer enters it.
    // docs/research/party-walls-2026-09-11.md.
    case "unheated-space":
    case "adjacent-room":
    case "party":
      return adjacency.temperatureC;
  }
}

function round(n: number, dp = 6): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

export function calculateRoomHeatLoss(
  room: Room,
  ctx: CalcContext,
  warnings: CalcWarning[],
): RoomHeatLoss {
  const tableMinimum = MIS_3005_D_TABLE_1.data[room.type];
  const designTempC = room.designTempC ?? tableMinimum;

  // MIS 3005-D §3.4.1(a): internal temperatures "not less than those specified in Table 1".
  if (designTempC < tableMinimum) {
    warnings.push({
      code: "MIS3005D_INTERNAL_TEMP_BELOW_TABLE_1",
      severity: "compliance-failure",
      subjectId: room.id,
      message:
        `Room "${room.name}" uses ${designTempC}°C but MIS 3005-D Table 1 requires ` +
        `not less than ${tableMinimum}°C for a ${room.type}.`,
    });
  }

  const surfaces: SurfaceHeatLoss[] = room.surfaces.map((s) => {
    const deltaTK = designTempC - otherSideTemperatureC(s.adjacency, ctx);
    const heatLossW = s.areaM2.value * s.uValue.value * deltaTK;

    // A negative difference means heat arrives through this surface rather
    // than leaving it. Whether that is worth saying out loud depends entirely
    // on what is on the other side.
    //
    // Between two HEATED spaces it is ordinary arithmetic and not a mistake at
    // all: a kitchen at 18 °C beside a bathroom at 22 °C is MIS 3005-D Table 1
    // working exactly as written. Saying "check the adjacency" about every one
    // of those walls buries the warnings that do mean something — one
    // three-room survey carried eight of them, all correct, above a real
    // bridging warning nobody would read.
    //
    // Against outside, the ground, or an unheated space it still means what it
    // always meant: a room colder than the outside air, or an "unheated" space
    // entered warmer than the heated room beside it, is a figure somebody typed
    // wrong, and no arithmetic downstream will notice.
    //
    // A surface that transmits nothing says nothing either way. Internal walls
    // and doors are taken at U = 0 unless the survey counts the heat moving
    // between its own heated rooms (migration 0057), and a surface carrying no
    // heat cannot have the direction of that heat questioned.
    const betweenHeatedSpaces =
      s.adjacency.kind === "adjacent-room" || s.adjacency.kind === "party";
    const transmits = s.uValue.value > 0 && s.areaM2.value > 0;

    if (deltaTK < 0 && transmits && !betweenHeatedSpaces) {
      warnings.push({
        code: "NEGATIVE_DELTA_T",
        severity: "warning",
        subjectId: s.id,
        message:
          `Surface "${s.name ?? s.id}" in "${room.name}" has a negative temperature ` +
          `difference (${round(deltaTK, 2)} K) — heat flows into the room. Check the adjacency.`,
      });
    }

    return {
      surfaceId: s.id,
      ...(s.name !== undefined ? { name: s.name } : {}),
      kind: s.kind,
      areaM2: s.areaM2.value,
      uValue: s.uValue.value,
      deltaTK: round(deltaTK),
      heatLossW: round(heatLossW),
    };
  });

  const transmissionW = surfaces.reduce((sum, s) => sum + s.heatLossW, 0);

  const bridgingFactor = ctx.thermalBridgingFactor ?? 0;
  const thermalBridgingW = transmissionW * bridgingFactor;

  // Ventilation loss: Φ_V = c_air × n × V × ΔT
  const cAir = ctx.airVolumetricHeatCapacity ?? DEFAULT_AIR_VOLUMETRIC_HEAT_CAPACITY;
  const ventDeltaTK = designTempC - ctx.externalDesignTempC;
  // The air flow is the room's air changes × volume — or, when its
  // continuously running extract takes out more, that: the extracted air is
  // replaced from outside and heated (Room.continuousExtractM3h).
  const airChangeFlowM3h = room.airChangesPerHour.value * room.volumeM3.value;
  const extractM3h = room.continuousExtractM3h ?? 0;
  const extractGoverns = extractM3h > airChangeFlowM3h;
  const ventilationAirM3h = extractGoverns ? extractM3h : airChangeFlowM3h;
  const ventilationW = cAir * ventilationAirM3h * ventDeltaTK;
  if (extractGoverns) {
    warnings.push({
      code: "CONTINUOUS_EXTRACT_SETS_VENTILATION",
      severity: "info",
      subjectId: room.id,
      message:
        `Room "${room.name}": its continuous extract (${round(extractM3h)} m³/h) takes out more air than its ` +
        `${room.airChangesPerHour.value} air changes an hour (${round(airChangeFlowM3h)} m³/h), so it sets the ventilation loss.`,
    });
  }

  const totalW = transmissionW + thermalBridgingW + ventilationW;
  const floorAreaM2 = room.floorAreaM2.value;

  if (floorAreaM2 <= 0) {
    warnings.push({
      code: "ZERO_FLOOR_AREA",
      severity: "warning",
      subjectId: room.id,
      message: `Room "${room.name}" has no floor area, so W/m² cannot be reported.`,
    });
  }

  return {
    roomId: room.id,
    name: room.name,
    designTempC,
    floorAreaM2,
    volumeM3: room.volumeM3.value,
    surfaces,
    transmissionW: round(transmissionW),
    thermalBridgingW: round(thermalBridgingW),
    ventilationW: round(ventilationW),
    ...(extractM3h > 0
      ? { ventilationBasis: extractGoverns ? ("continuous-extract" as const) : ("air-changes" as const), ventilationAirM3h: round(ventilationAirM3h) }
      : {}),
    totalW: round(totalW),
    wattsPerM2: floorAreaM2 > 0 ? round(totalW / floorAreaM2) : 0,
  };
}

/**
 * Room-by-room heat loss to BS EN 12831-1:2017 as required by MIS 3005-D §3.4.1(a).
 *
 * The result carries the engine and rules-bundle versions so it can be reproduced
 * years later during a certification-body audit.
 */
export function calculateDwellingHeatLoss(
  dwelling: Dwelling,
  ctx: CalcContext,
): DwellingHeatLoss {
  const warnings: CalcWarning[] = [];

  if (dwelling.rooms.length === 0) {
    warnings.push({
      code: "NO_ROOMS",
      severity: "compliance-failure",
      message: "The dwelling has no rooms, so no heat load can be calculated.",
    });
  }

  if (ctx.externalDesignTempColumn === "A") {
    warnings.push({
      code: "MIS3005D_INTERMITTENT_UPLIFT_CHECK",
      severity: "info",
      message:
        "Table 2 column A was used. MIS 3005-D §3.4.1(a) only waives the intermittent-heating " +
        "uplift when column B (99.6%) is used — confirm whether an uplift applies to this design.",
    });
  }

  if (ctx.thermalBridgingFactor === undefined) {
    warnings.push({
      code: "NO_THERMAL_BRIDGING_ALLOWANCE",
      severity: "warning",
      message:
        "No thermal bridging allowance has been decided. BS EN 12831-1 expects bridging to " +
        "be accounted for by psi-values or a blanket allowance — choose one under Design " +
        "conditions, or choose None to record that none applies.",
    });
  }

  // Unheated rooms come out here, before anything is calculated, so they
  // cannot reach a total by accident. Everything downstream — emitter
  // selection, the room outputs check, the room schedule in the design pack —
  // reads `rooms`, and so follows without knowing about the flag.
  const heated = dwelling.rooms.filter((r) => r.unheated !== true);
  const excludedRooms: ExcludedRoom[] = dwelling.rooms
    .filter((r) => r.unheated === true)
    .map((r) => ({
      roomId: r.id,
      name: r.name,
      floorAreaM2: r.floorAreaM2.value,
      ...(r.unheatedReason !== undefined ? { reason: r.unheatedReason } : {}),
    }));

  if (excludedRooms.length > 0) {
    warnings.push({
      code: "ROOMS_MARKED_UNHEATED",
      severity: "info",
      message:
        `${excludedRooms.length} room(s) are marked unheated and are not in this ` +
        `heat load or in emitter sizing: ` +
        `${excludedRooms.map((r) => r.name).join(", ")}. The heated rooms next to ` +
        `them still lose heat to them through their own walls.`,
    });
  }

  // Every room excluded is not "a very small heat load", it is no heat load at
  // all — and a heat pump sized to it would be sized to nothing. Said as a
  // compliance failure so a compliance run throws instead of issuing it.
  if (dwelling.rooms.length > 0 && heated.length === 0) {
    warnings.push({
      code: "NO_HEATED_ROOMS",
      severity: "compliance-failure",
      message:
        "Every room in the dwelling is marked unheated, so there is no heat load " +
        "to size a heat pump against.",
    });
  }

  const rooms = heated.map((r) => calculateRoomHeatLoss(r, ctx, warnings));

  const totalTransmissionW = rooms.reduce((s, r) => s + r.transmissionW, 0);
  const totalThermalBridgingW = rooms.reduce((s, r) => s + r.thermalBridgingW, 0);
  const totalVentilationW = rooms.reduce((s, r) => s + r.ventilationW, 0);
  const totalHeatLossW = rooms.reduce((s, r) => s + r.totalW, 0);
  const totalFloorAreaM2 = rooms.reduce((s, r) => s + r.floorAreaM2, 0);

  if (ctx.complianceMode) {
    const failures = warnings.filter((w) => w.severity === "compliance-failure");
    if (failures.length > 0) {
      throw new ComplianceError(
        "Heat loss calculation cannot be issued in compliance mode.",
        failures,
      );
    }
  }

  return {
    dwellingId: dwelling.id,
    rooms,
    excludedRooms,
    totalTransmissionW: round(totalTransmissionW),
    totalThermalBridgingW: round(totalThermalBridgingW),
    totalVentilationW: round(totalVentilationW),
    totalHeatLossW: round(totalHeatLossW),
    totalFloorAreaM2: round(totalFloorAreaM2),
    wattsPerM2: totalFloorAreaM2 > 0 ? round(totalHeatLossW / totalFloorAreaM2) : 0,
    warnings,
    stamp: {
      engineVersion: ENGINE_VERSION,
      rulesBundleVersion: RULES_BUNDLE_VERSION,
      calculatedAt: ctx.calculationDate,
      complianceMode: ctx.complianceMode,
    },
    context: ctx,
  };
}

export class ComplianceError extends Error {
  readonly failures: readonly CalcWarning[];
  constructor(message: string, failures: readonly CalcWarning[]) {
    super(`${message}\n${failures.map((f) => `  - [${f.code}] ${f.message}`).join("\n")}`);
    this.name = "ComplianceError";
    this.failures = failures;
  }
}
